/**
 * settings_myob_controller.js - MYOB integration panel Stimulus controller.
 *
 * Backend: /settings/myob/* (session-auth bridge over /api/integrations/myob/*).
 * Markup: templates/settings/_integrations.html (id='myob-panel').
 */

import { Controller } from "@hotwired/stimulus";

async function postJSON(url, body) {
    const resp = await window.api(url, {
        method: 'POST',
        body: body == null ? {} : body,
        responseType: 'raw',
        throwOnError: false,
        notifyError: false
    });
    let data = {};
    try { data = await resp.json(); } catch (_e) { /* non-JSON */ }
    return {ok: resp.ok, status: resp.status, data: data};
}

async function getJSON(url) {
    const resp = await window.api(url, {
        responseType: 'raw',
        throwOnError: false,
        notifyError: false
    });
    let data = {};
    try { data = await resp.json(); } catch (_e) { /* non-JSON */ }
    return {ok: resp.ok, status: resp.status, data: data};
}

function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function relativeTime(iso) {
    if (!iso) return 'never';
    const then = new Date(iso);
    if (isNaN(then.getTime())) return 'never';
    const sec = Math.round((Date.now() - then.getTime()) / 1000);
    if (sec < 60) return sec + 's ago';
    const min = Math.round(sec / 60);
    if (min < 60) return min + 'm ago';
    const hr = Math.round(min / 60);
    if (hr < 24) return hr + 'h ago';
    return Math.round(hr / 24) + 'd ago';
}

function statusBadge(status) {
    if (status === 'success') return '<span class="badge bg-success-subtle text-success">success</span>';
    if (status === 'error') return '<span class="badge bg-danger-subtle text-danger">error</span>';
    if (status === 'skipped') return '<span class="badge bg-secondary-subtle text-secondary">skipped</span>';
    return '<span class="badge bg-secondary">' + escapeHtml(status || '?') + '</span>';
}

export default class extends Controller {
    static targets = [
        'loading', 'panelMessage', 'disconnected', 'disconnectedBanner',
        'disconnectedBlurb', 'connectButton', 'connectLabel', 'connected',
        'companyFileName', 'lastSync', 'clientsSynced', 'clientsTotal',
        'invoicesSynced', 'invoicesTotal', 'disconnectButton', 'accountCode',
        'paymentAccountCode', 'configSaved', 'configError', 'refreshStatus',
        'syncAllButton', 'syncClientsButton', 'syncInvoicesButton', 'syncProgress',
        'syncProgressLabel', 'syncProgressBar', 'syncProgressErrorsWrap',
        'syncProgressErrors', 'syncLogBody', 'syncLogEmpty'
    ];

    connect() {
        this.bulkStream = null;
        this.streamOpened = false;
        this.configLoaded = false;
        this.currentStatus = null;
        this.lastBulkSyncInProgress = false;
        this.flashFromQueryParams();
        // Initial GET reconciles current truth (and attaches to any sync already
        // running — resume / other tab). The stream then carries live progress.
        this.refreshStatus({loadConfig: true});
        this.connectBulkStream(this.element);
    }

    disconnect() {
        // Close the stream so it doesn't outlive the panel's removal.
        this.disconnectBulkStream();
    }

    async connectMyob() {
        this.clearNotify();
        this.setButtonBusy(this.connectButtonTarget, true, '<span class="spinner-border spinner-border-sm me-2"></span>Connecting...');
        const result = await postJSON('/settings/myob/connect');
        if (result.ok && result.data && result.data.auth_url) {
            window.location.href = result.data.auth_url;
            return;
        }
        this.setButtonBusy(this.connectButtonTarget, false);
        this.notify((result.data && (result.data.error || result.data.message)) || 'Failed to start MYOB connection.', 'danger');
    }

    async disconnectMyob() {
        const message = 'Disconnect MYOB?\n\nChippie will stop syncing clients and invoices with MYOB. Your MYOB data is unaffected.';
        if (!window.confirm(message)) return;
        this.clearNotify();
        this.setButtonBusy(this.disconnectButtonTarget, true, '<span class="spinner-border spinner-border-sm me-1"></span>Disconnecting');
        const result = await postJSON('/settings/myob/disconnect');
        this.setButtonBusy(this.disconnectButtonTarget, false);
        if (result.ok && result.data && result.data.success) {
            this.configLoaded = false;
            this.notify('MYOB disconnected.', 'success');
            this.refreshStatus({loadConfig: false});
            return;
        }
        this.notify((result.data && result.data.error) || 'Failed to disconnect MYOB.', 'danger');
    }

    refreshLogs() {
        return this.loadLogs();
    }

    async refreshMappingOptions() {
        if (this.hasRefreshStatusTarget) {
            this.refreshStatusTarget.textContent = 'Refreshing...';
            this.refreshStatusTarget.hidden = false;
            this.refreshStatusTarget.classList.remove('text-success', 'text-danger');
        }
        const ok = await this.refreshConfig(true);
        if (this.hasRefreshStatusTarget) {
            this.refreshStatusTarget.textContent = ok ? 'Refreshed' : 'Refresh failed';
            this.refreshStatusTarget.classList.add(ok ? 'text-success' : 'text-danger');
            if (ok) window.setTimeout(() => { this.refreshStatusTarget.hidden = true; }, 2000);
        }
    }

    syncAll() {
        return this.startSync(['clients', 'invoices']);
    }

    syncClients() {
        return this.startSync(['clients']);
    }

    syncInvoices() {
        return this.startSync(['invoices']);
    }

    saveConfig() {
        return this.persistConfig();
    }

    show(target) { target.hidden = false; }

    hide(target) { target.hidden = true; }

    setText(target, value) { target.textContent = value; }

    notify(message, kind) {
        if (this.hasPanelMessageTarget) {
            this.panelMessageTarget.className = 'alert alert-' + (kind || 'info');
            this.panelMessageTarget.textContent = message;
            this.panelMessageTarget.hidden = false;
        }
        if (kind === 'success' && typeof window.showSuccessMessage === 'function') {
            window.showSuccessMessage(message);
        } else if (kind === 'danger' && typeof window.showErrorMessage === 'function') {
            window.showErrorMessage(message);
        }
    }

    clearNotify() {
        if (this.hasPanelMessageTarget) this.panelMessageTarget.hidden = true;
    }

    // Consume MYOB OAuth-result params on the post-callback landing, toast
    // through the controller's own notify(), then strip only myob/myob_error
    // from the URL so a refresh or back-nav does not re-fire the toast.
    flashFromQueryParams() {
        const params = new URLSearchParams(window.location.search);
        const connected = params.get('myob');
        const errorCode = params.get('myob_error');
        let consumed = false;

        if (connected === 'connected') {
            this.notify('MYOB connected.', 'success');
            consumed = true;
        }
        if (errorCode) {
            const errorMap = {
                auth_denied: 'MYOB authorization was denied. Please try again.',
                missing_params: 'MYOB callback was missing required parameters.',
                missing_business_id: 'MYOB did not return a company file. Please try again.',
                session_expired: 'Your MYOB connection session expired. Please try again.',
                auth_failed: 'MYOB authorization failed. Please try again.',
                unknown: 'An unexpected error occurred connecting MYOB.',
            };
            this.notify(errorMap[errorCode] || ('MYOB error: ' + errorCode), 'danger');
            consumed = true;
        }

        if (consumed) {
            params.delete('myob');
            params.delete('myob_error');
            const qs = params.toString();
            const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
            window.history.replaceState({}, '', newUrl);
        }
    }

    setButtonBusy(button, busy, label) {
        if (!button) return;
        button.disabled = busy;
        if (label) button.dataset.originalLabel = button.innerHTML;
        if (busy && label) button.innerHTML = label;
        if (!busy && button.dataset.originalLabel) {
            button.innerHTML = button.dataset.originalLabel;
            delete button.dataset.originalLabel;
        }
    }

    setSyncButtonsDisabled(disabled) {
        if (this.hasSyncAllButtonTarget) this.syncAllButtonTarget.disabled = disabled;
        if (this.hasSyncClientsButtonTarget) this.syncClientsButtonTarget.disabled = disabled;
        if (this.hasSyncInvoicesButtonTarget) this.syncInvoicesButtonTarget.disabled = disabled;
    }

    renderDisconnectedState(status) {
        const state = status.state || 'never_connected';
        if (state === 'never_connected') {
            this.disconnectedBannerTarget.hidden = true;
            this.disconnectedBlurbTarget.textContent = 'Connect your MYOB Business company file to push clients and invoices to MYOB. Payment status updates are checked by the scheduled poller.';
            this.connectLabelTarget.textContent = 'Connect MYOB';
            return;
        }
        if (state === 'disconnected') {
            this.disconnectedBannerTarget.hidden = true;
            this.disconnectedBlurbTarget.textContent = 'MYOB is disconnected. Reconnect to resume sync.';
            this.connectLabelTarget.textContent = 'Reconnect MYOB';
            return;
        }

        this.disconnectedBannerTarget.className = 'alert alert-warning';
        this.disconnectedBannerTarget.hidden = false;
        this.disconnectedBannerTarget.textContent = status.last_auth_error_message || 'MYOB needs to be reconnected before sync can continue.';
        this.disconnectedBlurbTarget.textContent = '';
        this.connectLabelTarget.textContent = 'Reconnect MYOB';
    }

    renderStatus(status) {
        this.currentStatus = status || null;
        this.hide(this.loadingTarget);
        if (!status || !status.connected) {
            this.hide(this.connectedTarget);
            this.setSyncButtonsDisabled(false);
            this.renderDisconnectedState(status || {});
            this.show(this.disconnectedTarget);
            return;
        }

        this.hide(this.disconnectedTarget);
        this.show(this.connectedTarget);
        this.setText(this.companyFileNameTarget, status.myob_company_file_name || status.myob_company_file_id || 'MYOB company file');
        this.setText(this.lastSyncTarget, relativeTime(status.last_synced_at));
        const stats = status.stats || {};
        const clients = stats.clients || {};
        const invoices = stats.invoices || {};
        this.setText(this.clientsSyncedTarget, clients.synced != null ? clients.synced : 0);
        this.setText(this.clientsTotalTarget, clients.total != null ? clients.total : 0);
        this.setText(this.invoicesSyncedTarget, invoices.synced != null ? invoices.synced : 0);
        this.setText(this.invoicesTotalTarget, invoices.total != null ? invoices.total : 0);

        if (status.bulk_sync_in_progress) {
            this.renderProgress(status);
            this.setSyncButtonsDisabled(true);
        } else {
            this.hide(this.syncProgressTarget);
            this.setSyncButtonsDisabled(false);
        }
    }

    renderProgress(status) {
        const stats = status.stats || {};
        const clients = stats.clients || {};
        const invoices = stats.invoices || {};
        const entity = status.bulk_sync_entity || 'clients';
        let total = 0;
        let synced = status.bulk_sync_baseline_synced || 0;
        if (entity === 'clients') total = clients.total || 0;
        if (entity === 'invoices') total = invoices.total || 0;
        if (entity === 'all') total = (clients.total || 0) + (invoices.total || 0);
        if (entity === 'clients') synced = clients.synced || synced;
        if (entity === 'invoices') synced = invoices.synced || synced;
        if (entity === 'all') synced = (clients.synced || 0) + (invoices.synced || 0);
        const pct = total > 0 ? Math.min(100, Math.round((synced / total) * 100)) : 0;
        this.syncProgressLabelTarget.textContent = entity;
        this.syncProgressBarTarget.style.width = pct + '%';
        this.syncProgressBarTarget.setAttribute('aria-valuenow', String(pct));
        this.syncProgressBarTarget.textContent = synced + ' / ' + total;
        this.syncProgressErrorsWrapTarget.hidden = true;
        this.show(this.syncProgressTarget);
    }

    finalizeProgress(processed, total, errors) {
        this.syncProgressBarTarget.style.width = '100%';
        this.syncProgressBarTarget.setAttribute('aria-valuenow', '100');
        this.syncProgressBarTarget.classList.remove('progress-bar-animated');
        this.syncProgressBarTarget.textContent = processed + ' / ' + total;
        if (errors > 0) {
            this.syncProgressErrorsTarget.textContent = String(errors);
            this.syncProgressErrorsWrapTarget.hidden = false;
        }
    }

    renderLogs(logs) {
        if (!logs || logs.length === 0) {
            this.syncLogBodyTarget.innerHTML = '';
            this.syncLogEmptyTarget.hidden = false;
            return;
        }
        this.syncLogEmptyTarget.hidden = true;
        this.syncLogBodyTarget.innerHTML = logs.map(function (log) {
            const entityId = log.entity_id == null ? '' : '#' + escapeHtml(log.entity_id);
            const entity = escapeHtml(log.entity_type) + entityId;
            const action = escapeHtml(log.action || log.sync_action || '');
            const when = escapeHtml(relativeTime(log.created_at));
            const errMsg = log.error_message ? escapeHtml(String(log.error_message).slice(0, 80)) : '';
            return '<tr><td>' + entity + '</td><td>' + action + '</td><td>' +
                   statusBadge(log.status || log.sync_status) + '</td><td>' + when +
                   '</td><td class="text-danger small">' + errMsg + '</td></tr>';
        }).join('');
    }

    populateSelect(selectEl, options, currentValue, placeholder) {
        selectEl.innerHTML = '';
        const first = document.createElement('option');
        first.value = '';
        first.textContent = placeholder;
        selectEl.appendChild(first);
        (options || []).forEach(function (opt) {
            const o = document.createElement('option');
            o.value = opt.uid || '';
            o.textContent = (opt.code ? opt.code + ' - ' : '') + (opt.name || 'Unnamed account');
            if (currentValue && o.value === currentValue) o.selected = true;
            selectEl.appendChild(o);
        });
    }

    // Apply one status payload — delivered by a Mercure 'myob_bulk_progress'
    // event, by the reconcile GET on each stream (re)connect, or by the mount
    // fetch in refreshStatus. Completion is edge-triggered on the in_progress
    // true->false transition (guarded by a prior in_progress=true sighting via
    // lastBulkSyncInProgress) so a resume landing after the sync already
    // finished never fires a spurious toast; the full log is fetched once then.
    onBulkStatus(data) {
        if (!data) return;
        const justFinished = this.lastBulkSyncInProgress && !data.bulk_sync_in_progress;
        this.renderStatus(data);
        this.lastBulkSyncInProgress = Boolean(data.bulk_sync_in_progress);
        if (justFinished) {
            this.loadLogs();
            this.notify('MYOB sync finished. Check recent activity for details.', 'success');
        }
    }

    async refreshStatus(options) {
        options = options || {};
        const result = await getJSON('/settings/myob/status');
        if (result.ok && result.data && result.data.success) {
            const data = result.data.data;
            this.onBulkStatus(data);
            if (data && data.connected) {
                this.loadLogs();
                if (options.loadConfig || !this.configLoaded) this.refreshConfig(false);
            }
        } else {
            this.hide(this.loadingTarget);
            this.show(this.disconnectedTarget);
            this.notify('Could not load MYOB status. Try refreshing the page.', 'danger');
        }
    }

    async loadLogs() {
        const result = await getJSON('/settings/myob/sync/logs?limit=20');
        if (result.ok && result.data && result.data.success) {
            this.renderLogs(result.data.logs || []);
        }
    }

    async refreshConfig(forceRefreshFromMyob) {
        const url = forceRefreshFromMyob ? '/settings/myob/config?refresh=1' : '/settings/myob/config';
        const result = await getJSON(url);
        if (!(result.ok && result.data && result.data.success)) {
            this.configErrorTarget.textContent = (result.data && result.data.error) || 'Failed to load MYOB accounts.';
            this.configErrorTarget.hidden = false;
            return false;
        }
        this.configErrorTarget.hidden = true;
        const cfg = result.data.data || {};
        this.populateSelect(this.accountCodeTarget, cfg.available_accounts, cfg.default_account_code, '-- Select revenue account --');
        this.populateSelect(this.paymentAccountCodeTarget, cfg.available_payment_accounts, cfg.default_payment_account_code, '-- Do not push payments --');
        this.configLoaded = true;
        return true;
    }

    async persistConfig() {
        this.configErrorTarget.hidden = true;
        const result = await postJSON('/settings/myob/config', {
            default_account_code: this.accountCodeTarget.value,
            default_payment_account_code: this.paymentAccountCodeTarget.value
        });
        if (result.ok && result.data && result.data.success) {
            this.configSavedTarget.hidden = false;
            window.setTimeout(() => { this.configSavedTarget.hidden = true; }, 1800);
            this.clearNotify();
        } else {
            const msg = (result.data && result.data.error) || 'Failed to save MYOB account settings.';
            this.configErrorTarget.textContent = msg;
            this.configErrorTarget.hidden = false;
            this.notify(msg, 'danger');
        }
    }

    // Open the page-scoped EventSource to the tenant's private bulk-sync topic.
    // The mercureAuthorization cookie (minted per request for business
    // principals) authorises the topic; withCredentials sends it. Native
    // EventSource auto-reconnects and replays via Last-Event-ID, so we let the
    // browser + hub heal the connection rather than hand-rolling backoff.
    connectBulkStream(el) {
        if (this.bulkStream) return;
        const hubUrl = el && el.dataset.myobHubUrl;
        const topic = el && el.dataset.myobTopic;
        if (!hubUrl || !topic) return;
        const sep = hubUrl.indexOf('?') === -1 ? '?' : '&';
        const url = hubUrl + sep + 'topic=' + encodeURIComponent(topic);
        const es = new EventSource(url, {withCredentials: true});
        es.addEventListener('myob_bulk_progress', (e) => {
            let data;
            try { data = JSON.parse(e.data); } catch (_err) { return; }
            this.onBulkStatus(data);
        });
        // 'open' fires on first connect AND every reconnect. Skip the first
        // (refreshStatus already reconciled on mount); reconcile only on a
        // genuine reconnect, which may have missed a completion while down.
        es.addEventListener('open', () => {
            if (!this.streamOpened) { this.streamOpened = true; return; }
            this.reconcileBulkStatus();
        });
        this.bulkStream = es;
    }

    disconnectBulkStream() {
        if (this.bulkStream) {
            this.bulkStream.close();
            this.bulkStream = null;
        }
        // Next mount opens a fresh stream; its first open is again covered by
        // that mount's refreshStatus, so the skip-first-open must reset here.
        this.streamOpened = false;
    }

    // One reconcile GET against current truth per stream (re)connect — closes
    // the only gap a pushed stream has: a sync that completes while the
    // connection is down emits no event to replay. Not a loop; fires per
    // connection, not on a cadence.
    async reconcileBulkStatus() {
        const result = await getJSON('/settings/myob/status');
        if (result.ok && result.data && result.data.success) {
            this.onBulkStatus(result.data.data);
        }
    }

    totalForStatus(status, entity) {
        const stats = (status && status.stats) || {};
        const clients = stats.clients || {};
        const invoices = stats.invoices || {};
        if (entity === 'clients') return clients.total || 0;
        if (entity === 'invoices') return invoices.total || 0;
        return (clients.total || 0) + (invoices.total || 0);
    }

    syncedForStatus(status, entity) {
        const stats = (status && status.stats) || {};
        const clients = stats.clients || {};
        const invoices = stats.invoices || {};
        if (entity === 'clients') return clients.synced || 0;
        if (entity === 'invoices') return invoices.synced || 0;
        return (clients.synced || 0) + (invoices.synced || 0);
    }

    async startSync(entities) {
        this.clearNotify();
        const entity = entities.length > 1 ? 'all' : (entities[0] || 'clients');
        if (entities.includes('invoices') && !this.accountCodeTarget.value) {
            this.notify('Choose a MYOB revenue account before syncing invoices.', 'danger');
            this.accountCodeTarget.focus();
            return;
        }

        this.setSyncButtonsDisabled(true);
        const totalBefore = this.totalForStatus(this.currentStatus, entity);
        const syncedBefore = this.syncedForStatus(this.currentStatus, entity);
        this.renderProgress({
            bulk_sync_entity: entity,
            bulk_sync_baseline_synced: syncedBefore,
            stats: this.currentStatus ? this.currentStatus.stats : {},
        });

        const result = await postJSON('/settings/myob/sync/all', {entities: entities});
        if (result.status === 412 && result.data && result.data.error_code === 'revenue_account_required') {
            this.setSyncButtonsDisabled(false);
            this.notify(result.data.error || 'Choose a MYOB revenue account before syncing invoices.', 'danger');
            await this.refreshConfig(false);
            this.accountCodeTarget.focus();
            return;
        }
        if (result.status === 202 && result.data && result.data.status === 'started') {
            this.notify('MYOB ' + entity + ' sync started.', 'success');
            // Backend claims the bulk slot before returning 202, so the next
            // stream event (or reconnect reconcile) already sees in_progress;
            // the optimistic renderProgress above holds until it arrives.
            this.lastBulkSyncInProgress = true;
            return;
        }
        if (result.status === 409 && result.data && result.data.status === 'already_running') {
            this.notify('A MYOB sync is already running. Attaching to progress.', 'warning');
            await this.refreshStatus({loadConfig: false});
            return;
        }
        if (result.status === 409 && result.data && result.data.status === 'aborted') {
            this.setSyncButtonsDisabled(false);
            this.finalizeProgress(0, totalBefore - syncedBefore, totalBefore - syncedBefore);
            this.notify(result.data.message || 'MYOB sync aborted before start.', 'danger');
            await this.refreshStatus({loadConfig: false});
            return;
        }

        this.setSyncButtonsDisabled(false);
        this.notify((result.data && result.data.error) || 'Failed to start MYOB sync.', 'danger');
    }
}
